import _fetchRetry from 'fetch-retry'
import isEmpty from 'lodash/isEmpty'
import env from 'src/env'
import { getNewToken, renewToken } from 'src/services/auth'
import 'src/types/types'
import { getHttpInit } from './api'
import { DEFAULT_ERROR_MSG, getErrorMessage } from './error'

const GEO_LOCATED_REGIONS = ['AMS', 'BRE', 'BEL', 'NLD', 'LUX', 'DEU']

export const fetchRetry = _fetchRetry(global.fetch, {
  retryOn: [500, 503],
  retries: 3,
  retryDelay: (attempt) => Math.pow(2, attempt) * 1000,
})

export async function httpGetRequest(callbackMethod) {
  let response
  try {
    let response = await callbackMethod()
    try {
      if (response.ok) {
        return await response.json()
      }
      switch (response.status) {
        case 401:
          const renewResult = await getNewToken()
          if (!renewResult.ok) {
            localStorage.clear()
            window.location.reload()
            break
          }
          const renewInfo = await renewResult.json()
          if (!renewInfo?.encodedToken) {
            localStorage.clear()
            window.location.reload()
            break
          }
          localStorage.setItem('token', renewInfo.encodedToken)
          return httpGetRequest(callbackMethod)
        default:
          return response.text().then((text) => {
            throw new Error(text)
          })
      }
    } catch (e) {
      return response.text().then((text) => {
        throw new Error(text)
      })
    }
  } catch (err) {
    try {
      let renewResult = await renewToken()
      if (renewResult.Success) {
        response = await callbackMethod()
        if (response.ok) {
          return await response.json()
        } else {
          throw new Error('jwt expired')
        }
      }
    } catch (_err) {
      return response.text().then((text) => {
        throw new Error(text)
      })
    }
  }
}

export async function httpPostRequest(callbackMethod) {
  let response
  try {
    let response = await callbackMethod()
    try {
      return await response.json()
    } catch (e) {
      return response.text().then((text) => {
        throw new Error(text)
      })
    }
  } catch (err) {
    try {
      let renewResult = await renewToken()
      if (renewResult.Success) {
        response = await callbackMethod()
        if (response.ok) {
          return await response.json()
        } else {
          throw new Error('jwt expired')
        }
      }
    } catch (_err) {
      return response.text().then((text) => {
        throw new Error(text)
      })
    }
  }
}

export const DEFAULT_ERROR_HANDLER = (setAlertComponent) => (error) => {
  if (!isEmpty(error?.message)) {
    try {
      error = JSON.parse(error.message)
    } catch (e) {
      setAlertComponent({
        open: true,
        severity: 'error',
        message: error.message,
      })
      return
    }
  }
  setAlertComponent({
    open: true,
    severity: 'error',
    message: getErrorMessage(error),
  })
}

export const handleErrorResponse = async (response, queryClient, queryInstance) => {
  const errorData = await response.json()
  const errorStatus = response.status
  if (queryInstance.failureCount > 0) {
    throw new Error(
      JSON.stringify({
        status: errorStatus,
        error_message: errorData.message || DEFAULT_ERROR_MSG,
      }),
    )
  }
  switch (errorStatus) {
    case 401:
    case 403: {
      const renewResult = await getNewToken()
      if (!renewResult.ok) {
        localStorage.clear()
        queryClient.invalidateQueries({ refetchType: 'none' })
        queryClient.clear()
        window.location.assign('/login')
      }
      const renewInfo = await renewResult.json()
      if (!renewInfo?.encodedToken) {
        localStorage.clear()
        queryClient.invalidateQueries({ refetchType: 'none' })
        queryClient.clear()
        window.location.assign('/login')
        return false
      }
      localStorage.setItem('token', renewInfo.encodedToken)
      if (queryInstance.failureCount === 0) {
        if (queryInstance.mutateAsync) {
          return queryInstance.mutateAsync()
        } else {
          return queryInstance.refetch()
        }
      }
      break
    }
    default:
      throw new Error(
        JSON.stringify({
          status: errorStatus,
          error_message: errorData.message || DEFAULT_ERROR_MSG,
        }),
      )
  }
}

export async function getRegionalConfigApi() {
  const init = getHttpInit('GET')
  return await httpGetRequest(() => fetch(`${env.API_URL}/api/regional-info`, init))
}

/**
 * Builds address parameters for workplace-related API calls
 * @param {Object} workplaceDetails - The workplace details object
 * @param {string} workplaceDetails.postCode - The postcode (required)
 * @param {string} [workplaceDetails.deliveryAddress1] - Address line 1 (optional)
 * @param {string} [workplaceDetails.deliveryAddress2] - Address line 2 (optional)
 * @param {string} [workplaceDetails.deliveryCity] - City (optional)
 * @returns {string} - URL-encoded address parameters string
 */
export const buildAddressParamsForWorkplace = (postCode, address1 = '', address2 = '', city = '') =>
  `postCode=${encodeURIComponent(postCode || '')}&address1=${encodeURIComponent(
    address1 || '',
  )}&address2=${encodeURIComponent(address2 || '')}&city=${encodeURIComponent(city || '')}`

/**
 * @param {string[]} connectedRegions
 * @returns {boolean}
 */
export const isGeoLocatedRegion = (connectedRegions = []) =>
  connectedRegions.some((region) => GEO_LOCATED_REGIONS.includes(region)) ||
  connectedRegions.length === 0
